import React, { useState, useEffect, useContext } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Layout from '../components/layout/Layout';
import { ThemeContext } from '../context/ThemeContext';
import { API_BASE } from '../api';

function DesignerTask() {
  const location = useLocation();
  const navigate = useNavigate();
  const { currentTheme } = useContext(ThemeContext);
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');
  const [designerName, setDesignerName] = useState('');


  const colors = {
    backgroundDark: '#0f0c29', 
    backgroundMedium: '#302b63', 
    backgroundLight: '#24243e',
    textLight: 'rgba(255, 255, 255, 0.8)',
    borderDark: 'rgba(255, 255, 255, 0.2)',
    primary: currentTheme?.primary || '#6366F1',
    secondary: currentTheme?.secondary || '#8B5CF6',
    accent: currentTheme?.accent || '#06B6D4',
    success: '#10B981',
    warning: '#F59E0B',
    danger: '#EF4444',
  };

  const videoTypeConfig = {
    'indoor_reel': { label: '🏠 Indoor Reel', color: '#6366F1' },
    'outdoor_reel': { label: '🌳 Outdoor Reel', color: '#10B981' },
    'podcast': { label: '🎙️ Podcast', color: '#F59E0B' },
    'indoor_long_video': { label: '🏠 Indoor Long', color: '#8B5CF6' },
    'outdoor_long_video': { label: '🌳 Outdoor Long', color: '#06B6D4' }
  };

  // ✅ NOTES FUNCTIONALITY - Tooltip component
  const Tooltip = ({ text, children, maxWidth = '300px' }) => {
    const [showTooltip, setShowTooltip] = useState(false);

    return (
      <div 
        style={{
          position: 'relative',
          display: 'inline-block',
          width: '100%'
        }}
        onMouseEnter={() => setShowTooltip(true)}
        onMouseLeave={() => setShowTooltip(false)}
      >
        {children}
        {showTooltip && text && (
          <div style={{
            visibility: 'visible',
            width: maxWidth,
            backgroundColor: '#1a1a2e',
            color: 'white',
            textAlign: 'center',
            borderRadius: '8px',
            padding: '10px',
            position: 'absolute',
            zIndex: 1000,
            bottom: '125%',
            left: '50%',
            transform: 'translateX(-50%)',
            opacity: 1,
            transition: 'opacity 0.3s',
            fontSize: '0.75rem',
            border: `1px solid ${colors.borderDark}`,
            backdropFilter: 'blur(10px)',
            boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word'
          }}>
            {text}
          </div>
        )}
      </div>
    );
  };

  // ✅ NOTES FUNCTIONALITY - Truncate text
  const truncateText = (text, maxLength = 15) => {
    if (!text || text === '-' || text === 'NULL' || text === 'null') return 'N/A';
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  const styles = {
    container: {
      minHeight: '100vh',
      background: `linear-gradient(135deg, ${colors.backgroundDark} 0%, ${colors.backgroundMedium} 50%, ${colors.backgroundLight} 100%)`,
      position: 'relative',
      overflowX: 'hidden'
    },
    animatedBackground: { position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', pointerEvents: 'none' },
    floatingOrb: { position: 'absolute', borderRadius: '50%', filter: 'blur(40px)', opacity: 0.3, animation: 'float 8s ease-in-out infinite' },
    orb1: { width: '300px', height: '300px', background: colors.primary, top: '10%', left: '5%', animationDelay: '0s' },
    orb2: { width: '200px', height: '200px', background: colors.secondary, top: '60%', right: '10%', animationDelay: '2s' },
    orb3: { width: '250px', height: '250px', background: colors.accent, bottom: '20%', left: '15%', animationDelay: '4s' },
    orb4: { width: '180px', height: '180px', background: colors.success, top: '30%', right: '20%', animationDelay: '6s' },
    gridOverlay: {
      position: 'absolute', top: 0, left: 0, width: '100%', height: '100%',
      backgroundImage: `linear-gradient(rgba(255,255,255,0.03) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.03) 1px, transparent 1px)`,
      backgroundSize: '50px 50px'
    },
    header: { padding: '30px 30px 20px', position: 'relative', zIndex: 2 },
    headerContent: { maxWidth: '1400px', margin: '0 auto' },
    greeting: { display: 'flex', alignItems: 'center', gap: '15px', marginBottom: '12px' },
    pageTitle: { fontSize: '2.2rem', fontWeight: '800', background: 'linear-gradient(135deg, #fff 0%, #a5b4fc 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', margin: 0 },
    welcomeBadge: { background: 'rgba(255, 255, 255, 0.1)', padding: '6px 12px', borderRadius: '16px', border: `1px solid ${colors.borderDark}`, backdropFilter: 'blur(10px)', display: 'flex', alignItems: 'center', gap: '6px' },
    badgeText: { color: 'white', fontWeight: '600', fontSize: '0.8rem' },
    badgeRole: { background: colors.primary, color: 'white', padding: '2px 6px', borderRadius: '10px', fontSize: '0.65rem', fontWeight: '600' },
    pageSubtitle: { color: colors.textLight, fontSize: '1rem', margin: 0 },
    content: { padding: '0 30px 30px', maxWidth: '1400px', margin: '0 auto', position: 'relative', zIndex: 2 },
    formCard: { background: 'rgba(255, 255, 255, 0.05)', borderRadius: '20px', border: `1px solid ${colors.borderDark}`, backdropFilter: 'blur(10px)', overflow: 'hidden' },
    cardHeader: { background: `linear-gradient(135deg, ${colors.primary}, ${colors.secondary})`, color: 'white', padding: '20px 25px' },
    cardTitle: { fontSize: '1.5rem', fontWeight: '700', margin: 0 },
    backBtn: { 
      background: 'rgba(255, 255, 255, 0.1)', 
      color: 'white', 
      border: `1px solid ${colors.borderDark}`, 
      padding: '10px 18px', 
      borderRadius: '10px', 
      fontWeight: '600', 
      fontSize: '0.9rem', 
      cursor: 'pointer', 
      display: 'flex', 
      alignItems: 'center', 
      gap: '6px', 
      textDecoration: 'none', 
      marginBottom: '15px',
      transition: 'all 0.3s ease'
    },
    loadingState: { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '80px 30px', color: 'white', textAlign: 'center' },
    spinner: { width: '18px', height: '18px', border: '2px solid transparent', borderTop: '2px solid white', borderRadius: '50%', animation: 'spin 1s linear infinite' },
    tableHeader: { background: 'rgba(255, 255, 255, 0.1)', color: 'white', borderBottom: `2px solid ${colors.borderDark}`, padding: '10px 8px', fontWeight: '600', fontSize: '0.8rem', whiteSpace: 'nowrap' },
    tableRow: { borderBottom: '1px solid rgba(255, 255, 255, 0.05)', fontSize: '0.8rem' },
    tableCell: { padding: '8px', verticalAlign: 'middle', fontSize: '0.8rem' },
    whiteTextCell: { padding: '8px', verticalAlign: 'middle', fontSize: '0.8rem', color: 'white' },
    actionBtn: { background: 'rgba(255, 255, 255, 0.1)', color: 'white', border: `1px solid ${colors.borderDark}`, padding: '8px 15px', borderRadius: '10px', fontWeight: '600', fontSize: '0.8rem', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px' },
    smallBadge: { padding: '3px 8px', borderRadius: '8px', fontSize: '0.7rem', fontWeight: '600' },
    linkButton: { 
      background: 'rgba(255, 255, 255, 0.1)', 
      color: 'white', 
      border: `1px solid ${colors.borderDark}`, 
      padding: '4px 8px', 
      borderRadius: '6px', 
      cursor: 'pointer', 
      fontSize: '0.7rem',
      transition: 'all 0.3s ease'
    },
    videoTypeBadge: { background: 'rgba(139, 92, 246, 0.2)', color: '#8B5CF6', padding: '3px 6px', borderRadius: '6px', fontSize: '0.7rem', fontWeight: '500' }
  };

  useEffect(() => {
    const designer = location.state?.designerName || 'Unknown Designer';
    setDesignerName(designer);
    fetchDesignerAllTasks(designer);
  }, [location.state]);

  const fetchDesignerAllTasks = async (designer) => {
    try {
      setLoading(true);
      setMessage('');
      const response = await fetch(`${API_BASE}/get_designer_task.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ designer_name: designer })
      });
      const data = await response.json();
      if (response.ok && data.success) {
        setTasks(data.tasks || []);
      } else {
        setMessage('❌ ' + (data.message || 'Failed to load tasks'));
      }
    } catch (error) {
      setMessage('❌ Error loading tasks: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  // ✅ CSV Download Function
  const downloadCSV = () => {
    if (tasks.length === 0) {
      setMessage('❌ No tasks to download');
      return;
    }

    // Define CSV headers
    const headers = [
      'Table',
      'ID',
      'Video Name',
      'Client',
      'Video Type',
      'Raw Link',
      'Thumbnail Link',
      'Designer Notes',
      'Status',
      'Created Date'
    ];

    // Map tasks to CSV rows
    const csvRows = tasks.map(task => {
      const row = [
        task.table_type || 'N/A',
        task.id || '',
        task.video_name || 'N/A',
        task.client_name || 'N/A',
        task.video_type || 'N/A',
        task.raw_link || 'N/A',
        task.thumbnail_link || 'N/A',
        task.designer_notes || 'N/A',
        task.status || 'N/A',
        task.created_at || 'N/A'
      ];
      
      // Escape quotes and wrap in quotes if contains comma
      return row.map(cell => {
        if (typeof cell === 'string' && (cell.includes(',') || cell.includes('"') || cell.includes('\n'))) {
          return `"${cell.replace(/"/g, '""')}"`;
        }
        return cell;
      }).join(',');
    });

    // Combine headers and rows
    const csvContent = [
      headers.join(','),
      ...csvRows
    ].join('\n');

    // Create blob and download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `${designerName.replace(/\s+/g, '_')}_tasks_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    setMessage('✅ CSV file downloaded successfully!');
  };

  const handleVideoNameClick = (task) => navigate('/task-full-details', { state: { task } });
  const handleClientClick = (clientName) => navigate('/client-tasks', { state: { clientName, filter: 'client' } });

  const getVideoTypeBadge = (videoType) => {
    if (!videoType) return <span className="badge" style={{...styles.videoTypeBadge, background: 'rgba(255,255,255,0.1)', color: 'white'}}>-</span>;
    const config = videoTypeConfig[videoType] || { label: videoType, color: '#6B7280' };
    return <span className="badge" style={{...styles.videoTypeBadge, background: `${config.color}20`, color: config.color}}>{config.label}</span>;
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { class: 'bg-warning text-dark', text: 'Pending', icon: 'fa-clock' },
      assigned: { class: 'bg-primary text-white', text: 'Assigned', icon: 'fa-user-check' },
      in_progress: { class: 'bg-info text-white', text: 'In Progress', icon: 'fa-spinner' },
      completed: { class: 'bg-success text-white', text: 'Completed', icon: 'fa-check' },
      approved: { class: 'bg-success text-white', text: 'Approved', icon: 'fa-check-circle' },
      unassigned: { class: 'bg-secondary text-white', text: 'Unassigned', icon: 'fa-user-times' }
    };
    const config = statusConfig[status] || statusConfig.pending;
    return <span className={`badge ${config.class}`} style={styles.smallBadge}><i className={`fas ${config.icon} me-1`}></i>{config.text}</span>;
  };

  const getTableBadge = (tableType) => {
    const tableConfig = {
      'tasks': { label: 'Tasks', color: colors.primary },
      'editor_tasks': { label: 'Editor Tasks', color: colors.success },
      'designer_tasks': { label: 'Designer Tasks', color: colors.accent }
    };
    const config = tableConfig[tableType] || { label: tableType, color: colors.secondary };
    return <span className="badge" style={{
      background: `${config.color}20`,
      color: config.color,
      padding: '3px 8px',
      borderRadius: '6px',
      fontSize: '0.7rem',
      fontWeight: '600'
    }}>{config.label}</span>;
  };

  const isValidLink = (link) => link && typeof link === 'string' && !['-', 'NULL', 'null', ''].includes(link.trim());
  
  const handleOpenLink = (link, type) => {
    if (link && link !== '-' && link !== 'NULL' && link !== 'null') {
      let formattedLink = link;
      if (!formattedLink.startsWith('http://') && !formattedLink.startsWith('https://')) {
        formattedLink = 'https://' + formattedLink;
      }
      window.open(formattedLink, '_blank', 'noopener,noreferrer');
    } else {
      setMessage(`❌ No ${type} link available`);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString || dateString === 'NULL' || dateString === '0000-00-00') return 'N/A';
    
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return 'Invalid Date';
      
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch (error) {
      console.error('Error formatting date:', error);
      return 'Invalid Date';
    }
  };

  if (loading) return <Layout><div style={styles.container}><div style={styles.loadingState}><div style={styles.spinner}></div><h3>Loading Designer Tasks</h3></div></div></Layout>;

  return (
    <Layout>
      <div style={styles.container}>
        <div className="animated-background" style={styles.animatedBackground}>
          <div className="floating-orb orb-1" style={{...styles.floatingOrb, ...styles.orb1}}></div>
          <div className="floating-orb orb-2" style={{...styles.floatingOrb, ...styles.orb2}}></div>
          <div className="floating-orb orb-3" style={{...styles.floatingOrb, ...styles.orb3}}></div>
          <div className="floating-orb orb-4" style={{...styles.floatingOrb, ...styles.orb4}}></div>
          <div className="grid-overlay" style={styles.gridOverlay}></div>
        </div>

        <div style={styles.header}>
          <div style={styles.headerContent}>
            <div style={styles.greeting}>
              <h1 style={styles.pageTitle}>Designer Tasks</h1>
              <div style={styles.welcomeBadge}>
                <span style={styles.badgeText}>All Tables View</span>
                <span style={styles.badgeRole}>Designer</span>
              </div>
            </div>
            <p style={styles.pageSubtitle}>All tasks handled by designer: <strong style={{color: colors.accent}}>{designerName}</strong> (From all tables)</p>
          </div>
        </div>

        <div style={styles.content}>
          <div style={{display: 'flex', gap: '12px', marginBottom: '15px', flexWrap: 'wrap'}}>
            <button style={styles.backBtn} onClick={() => navigate(-1)}>
              <i className="fas fa-arrow-left"></i> Back
            </button>
            <button 
              style={{...styles.backBtn, background: colors.accent}} 
              onClick={() => fetchDesignerAllTasks(designerName)}
            >
              <i className="fas fa-sync"></i> Refresh
            </button>
            <button 
              style={{...styles.backBtn, background: colors.success}} 
              onClick={downloadCSV}
              disabled={tasks.length === 0}
            >
              <i className="fas fa-download"></i> Download CSV
            </button>
          </div>

          {message && (
            <div style={{
              background: message.includes('✅') ? 'rgba(16, 185, 129, 0.1)' : 'rgba(239, 68, 68, 0.1)',
              color: message.includes('✅') ? colors.success : colors.danger,
              padding: '12px 16px',
              borderRadius: '10px',
              marginBottom: '15px',
              border: `1px solid ${message.includes('✅') ? colors.success : colors.danger}`,
              fontSize: '0.85rem'
            }}>
              <i className={`fas ${message.includes('✅') ? 'fa-check-circle' : 'fa-exclamation-triangle'} me-2`}></i>
              {message}
            </div>
          )}

          <div className="tasks-card" style={styles.formCard}>
            <div style={styles.cardHeader}>
              <h3 style={styles.cardTitle}>
                <i className="fas fa-palette me-2"></i>
                {designerName} - All Tasks ({tasks.length} tasks from {new Set(tasks.map(t => t.table_type)).size} tables)
              </h3>
            </div>
            <div style={{overflowX: 'auto'}}>
              <table className="table table-hover mb-0" style={{width: '100%', borderCollapse: 'collapse'}}>
                <thead>
                  <tr>
                    <th style={styles.tableHeader}>Table</th>
                    <th style={styles.tableHeader}>ID</th>
                    <th style={styles.tableHeader}>Video Name</th>
                    <th style={styles.tableHeader}>Client</th>
                    <th style={styles.tableHeader}>Video Type</th>
                    <th style={styles.tableHeader}>Raw Link</th>
                    <th style={styles.tableHeader}>Thumbnail</th>
                    <th style={styles.tableHeader}>Designer Notes</th>
                  </tr>
                </thead>
                <tbody>
                  {tasks.map((task, index) => {
                    const designerNotes = task.designer_notes || 'No notes';
                    
                    return (
                      <tr key={`${task.table_type}-${task.id}-${index}`} style={styles.tableRow}>
                        {/* Table Column */}
                        <td style={styles.tableCell}>
                          <Tooltip text={`From ${task.table_type} table`}>
                            {getTableBadge(task.table_type)}
                          </Tooltip>
                        </td>
                        
                        {/* ID Column */}
                        <td style={styles.whiteTextCell}>
                          <span style={{ color: 'white', fontWeight: 'bold', fontSize: '0.75rem' }}>
                            #{task.id}
                          </span>
                        </td>
                        
                        {/* Video Name Column */}
                        <td style={styles.whiteTextCell}>
                          <Tooltip text={task.video_name}>
                            <button 
                              className="btn btn-link p-0 text-decoration-none text-start"
                              onClick={() => handleVideoNameClick(task)}
                              style={{ 
                                color: 'white', 
                                fontWeight: '600', 
                                background: 'none', 
                                border: 'none', 
                                cursor: 'pointer',
                                fontSize: '0.75rem',
                                textAlign: 'left'
                              }}
                            >
                              {truncateText(task.video_name, 15)}
                            </button>
                          </Tooltip>
                        </td>
                        
                        {/* Client Column */}
                        <td style={styles.whiteTextCell}>
                          <Tooltip text={task.client_name}>
                            <button 
                              className="btn btn-link p-0 text-decoration-none"
                              onClick={() => handleClientClick(task.client_name)}
                              style={{ 
                                color: 'white', 
                                fontWeight: '600', 
                                background: 'none', 
                                border: 'none', 
                                cursor: 'pointer',
                                fontSize: '0.75rem'
                              }}
                            >
                              {truncateText(task.client_name, 12)}
                            </button>
                          </Tooltip>
                        </td>
                        
                        {/* Video Type Column */}
                        <td style={styles.tableCell}>
                          <Tooltip text={task.video_type ? `Video Type: ${task.video_type}` : 'Video type not specified'}>
                            {getVideoTypeBadge(task.video_type)}
                          </Tooltip>
                        </td>
                        
                        {/* Raw Link Column */}
                        <td style={styles.tableCell}>
                          <Tooltip text={task.raw_link || 'No raw link available'}>
                            <button
                              className="btn btn-sm"
                              onClick={() => handleOpenLink(task.raw_link, 'raw link')}
                              title={`Open raw link: ${task.raw_link || 'N/A'}`}
                              style={{
                                ...styles.linkButton,
                                background: isValidLink(task.raw_link) 
                                  ? `linear-gradient(135deg, ${colors.primary}, ${colors.secondary})`
                                  : 'rgba(255, 255, 255, 0.1)',
                                opacity: isValidLink(task.raw_link) ? 1 : 0.4,
                                cursor: isValidLink(task.raw_link) ? 'pointer' : 'not-allowed'
                              }}
                              onMouseEnter={(e) => {
                                if (isValidLink(task.raw_link)) {
                                  e.target.style.transform = 'translateY(-2px)';
                                  e.target.style.boxShadow = '0 4px 12px rgba(0,0,0,0.3)';
                                }
                              }}
                              onMouseLeave={(e) => {
                                if (isValidLink(task.raw_link)) {
                                  e.target.style.transform = 'translateY(0)';
                                  e.target.style.boxShadow = 'none';
                                }
                              }}
                            >
                              <i className={`fas ${isValidLink(task.raw_link) ? 'fa-external-link-alt' : 'fa-unlink'} me-1`}></i>
                              {isValidLink(task.raw_link) ? 'Open' : 'N/A'}
                            </button>
                          </Tooltip>
                        </td>
                        
                        {/* Thumbnail Column */}
                        <td style={styles.tableCell}>
                          <Tooltip text={task.thumbnail_link || 'No thumbnail link available'}>
                            <button
                              className="btn btn-sm"
                              onClick={() => handleOpenLink(task.thumbnail_link, 'thumbnail')}
                              title={`Open thumbnail: ${task.thumbnail_link || 'N/A'}`}
                              style={{
                                ...styles.linkButton,
                                background: isValidLink(task.thumbnail_link) 
                                  ? `linear-gradient(135deg, ${colors.accent}, ${colors.primary})`
                                  : 'rgba(255, 255, 255, 0.1)',
                                opacity: isValidLink(task.thumbnail_link) ? 1 : 0.4,
                                cursor: isValidLink(task.thumbnail_link) ? 'pointer' : 'not-allowed'
                              }}
                              onMouseEnter={(e) => {
                                if (isValidLink(task.thumbnail_link)) {
                                  e.target.style.transform = 'translateY(-2px)';
                                  e.target.style.boxShadow = '0 4px 12px rgba(0,0,0,0.3)';
                                }
                              }}
                              onMouseLeave={(e) => {
                                if (isValidLink(task.thumbnail_link)) {
                                  e.target.style.transform = 'translateY(0)';
                                  e.target.style.boxShadow = 'none';
                                }
                              }}
                            >
                              <i className={`fas ${isValidLink(task.thumbnail_link) ? 'fa-image' : 'fa-image'} me-1`}></i>
                              {isValidLink(task.thumbnail_link) ? 'View' : 'N/A'}
                            </button>
                          </Tooltip>
                        </td>
                        
                        {/* Designer Notes Column */}
                        <td style={styles.whiteTextCell}>
                          <Tooltip text={designerNotes}>
                            <span style={{ 
                              color: designerNotes !== 'No notes' ? 'white' : colors.textLight, 
                              cursor: designerNotes !== 'No notes' ? 'help' : 'default',
                              fontSize: '0.75rem'
                            }}>
                              {truncateText(designerNotes, 20)}
                            </span>
                          </Tooltip>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              
              {tasks.length === 0 && (
                <div style={{textAlign: 'center', padding: '50px 30px'}}>
                  <i className="fas fa-palette fa-3x mb-3" style={{ color: `${colors.accent}20` }}></i>
                  <h5 style={{ color: 'white', marginBottom: '12px', fontSize: '1.2rem' }}>No Design Tasks</h5>
                  <p style={{ color: colors.textLight, marginBottom: '20px', fontSize: '0.9rem' }}>
                    No tasks assigned to designer <strong>{designerName}</strong> in any table.
                  </p>
                  <button 
                    className="btn"
                    onClick={() => fetchDesignerAllTasks(designerName)}
                    style={{
                      ...styles.backBtn,
                      width: 'auto',
                      background: colors.accent
                    }}
                  >
                    <i className="fas fa-sync me-2"></i>
                    Refresh
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      <style jsx>{`
        @keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
        @keyframes float { 0%, 100% { transform: translate(0px, 0px) scale(1); } 33% { transform: translate(30px, -50px) scale(1.1); } 66% { transform: translate(-20px, 20px) scale(0.9); } }
        .table-hover tbody tr:hover { background: rgba(255, 255, 255, 0.05) !important; }
        button:hover { transform: translateY(-2px); box-shadow: 0 4px 12px rgba(0,0,0,0.3); }
      `}</style>
    </Layout>
  );
}

export default DesignerTask;